import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { 
  Shield, Users, Database,
  MoreVertical, UserCog, Ban, CheckCircle, ShieldCheck,
  Mail, Calendar, AlertTriangle, Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchData();
    } else if (user) {
      setLoading(false);
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/users`, { headers })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/seed-data`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Sample data created successfully!');
      fetchData();
    } catch (error) {
      console.error('Failed to seed data:', error);
      alert('Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  const handleDisableUser = async (userId) => {
    if (!window.confirm('Disable this user? They will not be able to log in until re-enabled.')) return;
    
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/disable`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to disable user:', error);
      alert(error.response?.data?.detail || 'Failed to disable user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEnableUser = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/enable`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to enable user:', error);
      alert(error.response?.data?.detail || 'Failed to enable user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleChangeRole = async () => {
    if (!editingUser || !selectedRole) return;
    
    setActionLoading(editingUser.id);
    try {
      await axios.post(`${API}/admin/users/${editingUser.id}/change-role?new_role=${selectedRole}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowRoleModal(false);
      setEditingUser(null);
      setSelectedRole('');
      fetchData();
    } catch (error) {
      console.error('Failed to change role:', error);
      alert(error.response?.data?.detail || 'Failed to change role');
    } finally {
      setActionLoading(null);
    }
  };

  const openRoleModal = (targetUser) => {
    setEditingUser(targetUser);
    setSelectedRole(targetUser.role);
    setShowRoleModal(true);
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Permanently delete user "${userEmail}"? This action cannot be undone.`)) return;
    
    setActionLoading(userId);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  // Categorize users
  const activeUsers = users.filter(u => !u.is_disabled);
  const disabledUsers = users.filter(u => u.is_disabled);
  const adminUsers = users.filter(u => u.role === 'admin');
  const regularUsers = users.filter(u => u.role === 'user');

  if (user?.role !== 'admin') {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Shield className="h-12 w-12 sm:h-16 sm:w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl sm:text-2xl font-bold text-carbon-black mb-2">Access Denied</h2>
            <p className="text-sm sm:text-base text-slate-gray">You don't have permission to access this page.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64" data-testid="admin-loading">
          <div className="text-xl text-slate-gray">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  const UserCard = ({ targetUser, showActions = true }) => {
    const isCurrentUser = targetUser.id === user.id;
    const isSuperuser = targetUser.is_superuser;
    const canModify = showActions && !isCurrentUser && !isSuperuser;
    
    return (
      <div
        className={`p-3 sm:p-4 rounded-lg ${
          targetUser.is_disabled ? 'bg-red-50 border border-red-200' : 'bg-cloud-white'
        }`}
        data-testid={`user-item-${targetUser.id}`}
      >
        {/* Mobile Layout */}
        <div className="sm:hidden">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                isSuperuser ? 'bg-yellow-100' : targetUser.role === 'admin' ? 'bg-purple-100' : 'bg-blue-100'
              }`}>
                {isSuperuser ? (
                  <Shield className="h-5 w-5 text-yellow-600" />
                ) : targetUser.role === 'admin' ? (
                  <ShieldCheck className="h-5 w-5 text-purple-600" />
                ) : (
                  <Users className="h-5 w-5 text-blue-600" />
                )}
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-carbon-black text-sm truncate">{targetUser.first_name} {targetUser.last_name}</h3>
                <p className="text-xs text-slate-gray truncate">{targetUser.email}</p>
              </div>
            </div>
            {canModify && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0 flex-shrink-0"
                    disabled={actionLoading === targetUser.id}
                    data-testid={`user-menu-${targetUser.id}`}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem 
                    onClick={() => openRoleModal(targetUser)}
                    className="cursor-pointer"
                    data-testid={`change-role-${targetUser.id}`}
                  >
                    <UserCog className="h-4 w-4 mr-2 text-blue-600" />
                    <span>Change Role</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {targetUser.is_disabled ? (
                    <>
                      <DropdownMenuItem 
                        onClick={() => handleEnableUser(targetUser.id)}
                        className="cursor-pointer text-green-600 focus:text-green-600"
                        data-testid={`mobile-enable-user-${targetUser.id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        <span>Enable User</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => handleDeleteUser(targetUser.id, targetUser.email)}
                        className="cursor-pointer text-red-600 focus:text-red-600"
                        data-testid={`mobile-delete-user-${targetUser.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        <span>Delete User</span>
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <DropdownMenuItem 
                      onClick={() => handleDisableUser(targetUser.id)}
                      className="cursor-pointer text-red-600 focus:text-red-600"
                      data-testid={`disable-user-${targetUser.id}`}
                    >
                      <Ban className="h-4 w-4 mr-2" />
                      <span>Disable User</span>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
              isSuperuser ? 'bg-yellow-100 text-yellow-800' : 
              targetUser.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
            }`}>
              {isSuperuser ? '⭐ Super' : targetUser.role === 'admin' ? '👑 Admin' : '👤 User'}
            </span>
            {isSuperuser && (
              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                Protected
              </span>
            )}
            {isCurrentUser && !isSuperuser && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">You</span>
            )}
            {targetUser.is_disabled && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full flex items-center">
                <Ban className="h-3 w-3 mr-1" />
                Disabled
              </span>
            )}
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden sm:flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isSuperuser ? 'bg-yellow-100' : targetUser.role === 'admin' ? 'bg-purple-100' : 'bg-blue-100'
            }`}>
              {isSuperuser ? (
                <Shield className="h-5 w-5 text-yellow-600" />
              ) : targetUser.role === 'admin' ? (
                <ShieldCheck className="h-5 w-5 text-purple-600" />
              ) : (
                <Users className="h-5 w-5 text-blue-600" />
              )}
            </div>
            <div>
              <div className="flex items-center space-x-2 flex-wrap">
                <h3 className="font-semibold text-carbon-black">{targetUser.first_name} {targetUser.last_name}</h3>
                {isSuperuser && (
                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full flex items-center">
                    <Shield className="h-3 w-3 mr-1" />
                    Superuser
                  </span>
                )}
                {isCurrentUser && !isSuperuser && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">You</span>
                )}
                {targetUser.is_disabled && (
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full flex items-center">
                    <Ban className="h-3 w-3 mr-1" />
                    Disabled
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-4 text-sm text-slate-gray">
                <span className="flex items-center">
                  <Mail className="h-3 w-3 mr-1" />
                  {targetUser.email}
                </span>
                <span className="flex items-center">
                  <Calendar className="h-3 w-3 mr-1" />
                  Joined {new Date(targetUser.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              isSuperuser ? 'bg-yellow-100 text-yellow-800' : 
              targetUser.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
            }`}>
              {isSuperuser ? '⭐ Superuser' : targetUser.role === 'admin' ? '👑 Admin' : '👤 User'}
            </span>
            
            {canModify && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0"
                    disabled={actionLoading === targetUser.id}
                    data-testid={`user-menu-${targetUser.id}`}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem 
                    onClick={() => openRoleModal(targetUser)}
                    className="cursor-pointer"
                    data-testid={`change-role-${targetUser.id}`}
                  >
                    <UserCog className="h-4 w-4 mr-2 text-blue-600" />
                    <span>Change Role</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {targetUser.is_disabled ? (
                    <>
                      <DropdownMenuItem 
                        onClick={() => handleEnableUser(targetUser.id)}
                        className="cursor-pointer text-green-600 focus:text-green-600"
                        data-testid={`enable-user-${targetUser.id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        <span>Enable User</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => handleDeleteUser(targetUser.id, targetUser.email)}
                        className="cursor-pointer text-red-600 focus:text-red-600"
                        data-testid={`delete-user-${targetUser.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        <span>Delete User</span>
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <DropdownMenuItem 
                      onClick={() => handleDisableUser(targetUser.id)}
                      className="cursor-pointer text-red-600 focus:text-red-600"
                      data-testid={`disable-user-${targetUser.id}`}
                    >
                      <Ban className="h-4 w-4 mr-2" />
                      <span>Disable User</span>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 sm:space-y-8" data-testid="admin-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-carbon-black mb-1 sm:mb-2" data-testid="admin-title">Admin Panel</h1>
            <p className="text-sm sm:text-base text-slate-gray">System overview and user management</p>
          </div>
          <Button
            onClick={handleSeedData}
            disabled={seeding}
            className="gradient-fearless text-white w-full sm:w-auto"
            data-testid="seed-data-btn"
          >
            <Database className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
            {seeding ? 'Seeding...' : 'Seed Sample Data'}
          </Button>
        </div>

        {/* Admin Stats Grid - User Management Stats Only */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
          <Card className="shadow-lg rounded-xl border-0 card-hover">
            <CardContent className="p-3 sm:p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-slate-gray font-medium mb-1 truncate">Total Users</p>
                  <p className="text-lg sm:text-2xl lg:text-3xl font-bold text-carbon-black">{users.length}</p>
                  <p className="text-xs text-slate-gray mt-1 hidden sm:block">
                    {adminUsers.length} admins • {regularUsers.length} users
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
                  <Users className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-fearless-blue" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg rounded-xl border-0 card-hover">
            <CardContent className="p-3 sm:p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-slate-gray font-medium mb-1 truncate">Active</p>
                  <p className="text-lg sm:text-2xl lg:text-3xl font-bold text-green-600">{activeUsers.length}</p>
                </div>
                <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg rounded-xl border-0 card-hover">
            <CardContent className="p-3 sm:p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-slate-gray font-medium mb-1 truncate">Disabled</p>
                  <p className="text-lg sm:text-2xl lg:text-3xl font-bold text-red-600">{disabledUsers.length}</p>
                </div>
                <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
                  <Ban className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users Management with Tabs */}
        <Card className="shadow-lg rounded-xl border-0">
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-carbon-black flex items-center" data-testid="users-list-title">
              <UserCog className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
              User Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all" className="w-full">
              {/* Mobile Tab List - Scrollable */}
              <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                <TabsList className="grid w-full grid-cols-4 mb-4 sm:mb-6 min-w-[400px] sm:min-w-0">
                  <TabsTrigger value="all" className="text-xs sm:text-sm px-2 sm:px-4" data-testid="tab-all-users">
                    All ({users.length})
                  </TabsTrigger>
                  <TabsTrigger value="admins" className="text-xs sm:text-sm px-2 sm:px-4" data-testid="tab-admins">
                    <ShieldCheck className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    <span className="hidden sm:inline">Admins</span> ({adminUsers.length})
                  </TabsTrigger>
                  <TabsTrigger value="users" className="text-xs sm:text-sm px-2 sm:px-4" data-testid="tab-users">
                    <Users className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    <span className="hidden sm:inline">Users</span> ({regularUsers.length})
                  </TabsTrigger>
                  <TabsTrigger value="disabled" className="text-xs sm:text-sm px-2 sm:px-4" data-testid="tab-disabled">
                    <Ban className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    <span className="hidden sm:inline">Disabled</span> ({disabledUsers.length})
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="all" className="space-y-2 sm:space-y-3">
                {users.length === 0 ? (
                  <p className="text-slate-gray text-center py-8 text-sm sm:text-base">No users found</p>
                ) : (
                  users.map((u) => <UserCard key={u.id} targetUser={u} />)
                )}
              </TabsContent>

              <TabsContent value="admins" className="space-y-2 sm:space-y-3">
                {adminUsers.length === 0 ? (
                  <p className="text-slate-gray text-center py-8 text-sm sm:text-base">No admin users</p>
                ) : (
                  adminUsers.map((u) => <UserCard key={u.id} targetUser={u} />)
                )}
              </TabsContent>

              <TabsContent value="users" className="space-y-2 sm:space-y-3">
                {regularUsers.length === 0 ? (
                  <p className="text-slate-gray text-center py-8 text-sm sm:text-base">No regular users</p>
                ) : (
                  regularUsers.map((u) => <UserCard key={u.id} targetUser={u} />)
                )}
              </TabsContent>

              <TabsContent value="disabled" className="space-y-2 sm:space-y-3">
                {disabledUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 text-green-500 opacity-50" />
                    <p className="text-slate-gray text-sm sm:text-base">No disabled users</p>
                    <p className="text-xs sm:text-sm text-slate-gray mt-1">All users are currently active</p>
                  </div>
                ) : (
                  <>
                    <Card className="border-yellow-300 bg-yellow-50 mb-3 sm:mb-4">
                      <CardContent className="p-3 sm:p-4">
                        <div className="flex items-start sm:items-center space-x-3">
                          <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600 flex-shrink-0 mt-0.5 sm:mt-0" />
                          <p className="text-xs sm:text-sm text-yellow-800">
                            <strong>Note:</strong> Disabled users cannot log in until their account is re-enabled.
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                    {disabledUsers.map((u) => <UserCard key={u.id} targetUser={u} />)}
                  </>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Role Change Modal */}
        <Dialog open={showRoleModal} onOpenChange={setShowRoleModal}>
          <DialogContent className="sm:max-w-md" data-testid="role-change-modal">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl">Change User Role</DialogTitle>
              <DialogDescription className="text-sm">
                Change the role for {editingUser?.first_name} {editingUser?.last_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-carbon-black">Select Role</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setSelectedRole('user')}
                    className={`p-3 sm:p-4 rounded-lg border-2 transition-all ${
                      selectedRole === 'user' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="select-role-user"
                  >
                    <Users className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-2 text-blue-600" />
                    <p className="font-semibold text-carbon-black text-sm sm:text-base">User</p>
                    <p className="text-xs text-slate-gray mt-1 hidden sm:block">Standard access to own projects</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedRole('admin')}
                    className={`p-3 sm:p-4 rounded-lg border-2 transition-all ${
                      selectedRole === 'admin' 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="select-role-admin"
                  >
                    <ShieldCheck className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-2 text-purple-600" />
                    <p className="font-semibold text-carbon-black text-sm sm:text-base">Admin</p>
                    <p className="text-xs text-slate-gray mt-1 hidden sm:block">Full system access</p>
                  </button>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1 order-2 sm:order-1"
                  onClick={() => {
                    setShowRoleModal(false);
                    setEditingUser(null);
                    setSelectedRole('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 gradient-fearless text-white order-1 sm:order-2"
                  onClick={handleChangeRole}
                  disabled={actionLoading === editingUser?.id || selectedRole === editingUser?.role}
                  data-testid="confirm-role-change"
                >
                  {actionLoading === editingUser?.id ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminPage;
